// Simulador: "o que o piloto teria feito nos últimos 30 dias".
import { useState } from 'react'
import { FlaskConical, ShieldCheck, TriangleAlert } from 'lucide-react'
import type { AutopilotMode } from '@impulso/shared'
import { brl } from '@impulso/shared'
import { errorMessage } from '../../lib/api'
import { useSimulate } from './api'

const MODE_LABEL: Record<AutopilotMode, string> = { suggest: 'Só sugerir', copilot: 'Copiloto', auto: 'Automático' }

export default function SimulatorCard({ companyId, defaultMode }: { companyId: string; defaultMode: AutopilotMode }) {
  const [mode, setMode] = useState<AutopilotMode>(defaultMode === 'suggest' ? 'copilot' : defaultMode)
  const [days, setDays] = useState(30)
  const sim = useSimulate(companyId)
  const r = sim.data
  const activeDays = r?.days.filter((d) => d.executed.length || d.recommended.length || d.blocked.length) ?? []
  return (
    <div className="card">
      <div className="row" style={{ gap: 10 }}>
        <span className="ic purple"><FlaskConical size={18} /></span>
        <div>
          <h3>Simulador</h3>
          <div className="small muted">Veja o que o piloto teria feito com os seus dados reais, sem mudar nada.</div>
        </div>
      </div>
      <div className="row wrap mt-16" style={{ gap: 8 }}>
        <select className="input" style={{ width: 'auto' }} aria-label="Modo para simular" value={mode} onChange={(e) => setMode(e.target.value as AutopilotMode)}>
          {(['suggest', 'copilot', 'auto'] as const).map((m) => <option key={m} value={m}>{MODE_LABEL[m]}</option>)}
        </select>
        <select className="input" style={{ width: 'auto' }} aria-label="Período" value={days} onChange={(e) => setDays(Number(e.target.value))}>
          {[7, 30, 60, 90].map((d) => <option key={d} value={d}>Últimos {d} dias</option>)}
        </select>
        <button className="btn primary sm" disabled={sim.isPending} onClick={() => sim.mutate({ mode, days })}>{sim.isPending ? 'Simulando…' : 'Simular'}</button>
      </div>
      {sim.isError && <p className="small mt-12" style={{ color: 'var(--red)' }}>{errorMessage(sim.error)}</p>}
      {r && (
        <div className="stack mt-16" style={{ gap: 12 }}>
          <div className={`callout ${r.checks.guardrailsOk ? 'green' : 'amber'}`}>
            {r.checks.guardrailsOk ? <ShieldCheck size={18} /> : <TriangleAlert size={18} />}
            <span className="small">
              {r.checks.guardrailsOk ? 'Nenhum limite de segurança teria sido ultrapassado.' : `${r.checks.violations.length} problema(s) encontrados: ${r.checks.violations[0]?.problem}`}
            </span>
          </div>
          <div className="grid g-3" style={{ gap: 8 }}>
            <div><div className="strong" style={{ fontSize: 20 }}>{r.summary.executed}</div><div className="small muted">mudanças feitas sozinho</div></div>
            <div><div className="strong" style={{ fontSize: 20 }}>{r.summary.recommended}</div><div className="small muted">pediriam sua aprovação</div></div>
            <div><div className="strong" style={{ fontSize: 20 }}>{r.summary.blocked}</div><div className="small muted">barradas pelos limites</div></div>
          </div>
          {r.summary.budgetChange !== 0 && <div className="small muted">Variação no orçamento diário total: {r.summary.budgetChange > 0 ? '+' : ''}{brl(r.summary.budgetChange, 2)}</div>}
          {activeDays.length === 0 && <p className="small muted">Nesse período o piloto não teria feito nada. Isso é comum com pouco histórico.</p>}
          <div className="list" style={{ maxHeight: 320, overflowY: 'auto' }}>
            {activeDays.slice().reverse().map((d) => (
              <div className="list-item" key={d.date} style={{ flexDirection: 'column', alignItems: 'stretch', gap: 4 }}>
                <div className="small strong">{d.date.split('-').reverse().join('/')}</div>
                {d.executed.map((x, i) => <div className="small" key={`e${i}`}>✓ {x.title}</div>)}
                {d.recommended.map((x, i) => <div className="small muted" key={`r${i}`}>? {x.title} <span className="muted">({x.reason ?? 'pede aprovação'})</span></div>)}
                {d.blocked.map((x, i) => <div className="small muted" key={`b${i}`}>✕ {x.title}: {x.reason}</div>)}
              </div>
            ))}
          </div>
          <ul className="small muted" style={{ paddingLeft: 18 }}>{r.assumptions.map((a) => <li key={a}>{a}</li>)}</ul>
        </div>
      )}
    </div>
  )
}
